import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import TabBar from "../../components/tab-bar/TabBar";
import TabContainer from "../../components/tab-container/TabContainer";
import TabWithCount from "../../components/tab-with-count/TabWithCount";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IOrganization } from "../../services/organization-data-service/Models";
import { IGroupViewState } from "../../services/state-data-service/Models";
import UICustomizationPresetList, { UICustomizationPresetListRefType } from "./ui-customization-preset-list/UICustomizationPresetList";
import { actionItems } from "./UICustomizationPresetAdminViewActions";
import { UICustomizationPresetAdminViewTabEnum, useUICustomizationPresetAdminContext } from "./UICustomizationPresetAdminViewState";

export default function UICustomizationPresetAdminView() {

    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    const { t } = useTranslation();

    const {
        state: {
            activeTab,
            organizationID,
            presetCount,
            viewOptionState
        },
        setActiveTab,
        setOrganizationID
    } = useUICustomizationPresetAdminContext();

    const { data: objectViewState, status } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group
        } as IeLynxObject,
    });

    const groupViewState = objectViewState ? objectViewState as IGroupViewState : undefined;

    const presetListRef = useRef<UICustomizationPresetListRefType>(null);

    function onExecuteAction(actionItem: ActionMenuItem) {

        const action = actionItem.action as BaseActionEnum;

        presetListRef.current?.executeAction(action);
    }

    function onOrganizationSelected(item: IOrganization) {
        setOrganizationID(item.id);
    }

    const currentOrganizationID = organizationID ?? groupViewState?.organizationID;

    return (
        <ContentView>
            <ContextualAppBar
                hideBreadcrumb={true}
                viewOptionState={viewOptionState}
                actions={actionItems}
                onExecuteAction={onExecuteAction}
                showOrganizationPicker={true}
                onOrganizationSelected={onOrganizationSelected}
                organizationID={currentOrganizationID}
            />

            <TabBar
                value={activeTab}
                onChange={(_event, value) => setActiveTab(value)}
            >
                <TabWithCount label={t("uiCustomizationPresetAdministration.tabs.presets.title") as string}
                    count={presetCount}
                    countDisabled={activeTab !== UICustomizationPresetAdminViewTabEnum.Presets}
                    value={UICustomizationPresetAdminViewTabEnum.Presets}
                />
            </TabBar>

            <LoadingZone isLoading={status === "loading"} >
                <TabContainer visible={true}>
                    <UICustomizationPresetList
                        ref={presetListRef}
                        organizationID={currentOrganizationID}
                    />
                </TabContainer>
            </LoadingZone>
        </ContentView>
    );
}
